import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import axios from "axios";
import { ChakraProvider } from "@chakra-ui/react";
import "focus-visible/dist/focus-visible";
import store from "../store.js";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  ROOT,
  LOGIN,
  REGISTER_PATH,
  FORGOT_PASSWORD_PATH,
  USER_HOME,
  STORY_DETAIL_PATH,
  REGISTRATION_EMAIL_SENT,
  CONFIRM_EMAIL,
  STORY,
  PROJECTS,
  LAYOUTS,
  PASSWORD_RESET_EMAIL_SENT,
  SETTINGS,
  PREVIEW_PATH,
  CREATE_WORKSPACE,
  ADD_NEW_MEMBERS,
  BRANDKIT,
  OAUTH_SHOPIFY_CALLBACK,
  STORY_PAGE_PATH,
  SHOPIFY_LOGIN,
  MY_ASSETS,
  MY_TEMPLATES,
  MY_COMPONENTS,
  CONFIRM_PASSWORD_RESET,
  PASSWORD_RESET_SUCCESS,
  PASSWORD_RESET_FAILURE,
  REGISTRATION_VERIFICATION_SUCCESS,
  REGISTRATION_VERIFICATION_FAILURE,
  QUICK_START,
  MY_FOLDERS,
  TEMPLATES_SCREEN,
  SEARCH_TEMPLATES_SCREEN,
  CREATE_PASSWORD_VIA_INVITATION,
  PASSWORD_SET_SUCCESS_VIA_INVITATION,
  PASSWORD_SET_FAILURE_VIA_INVITATION,
  OAUTH_GENERIC_WAITING_SCREEN,
  MAGICAL,
  MY_COPIES,
  AI_NEW_DOCUMENT,
  RENDER_PREVIEW,
  TEMPLATES_BY_FORMAT_SCREEN_PATH,
  TEMPLATES_BY_CATEGORY_SCREEN_PATH,
  ACCESS_DENIED,
  INTERNAL_SERVER_ERROR,
  DOCUMENTS,
  STORY_DETAIL_NAME_PATH,
  LIFETIME,
  AI_TEMPLATE_PATH,
  AI_DOCUMENT_PATH,
  PUBLISHING,
  PLANNER,
  ANALYTICS,
  ANALYTICS_GENERATE_REPORT,
  DRAFT,
  POST,
  EXTENSION_PLANNER_NEW_POST,
  ALL_TEMPLATES_BY_FORMAT_PATH,
  UNAUTHORIZED,
  NOT_FOUND,
  MY_PROJECTS,
  SHARED_WITH_ME_PROJECTS,
  MY_DOCUMENTS,
  SHARED_WITH_ME_DOCUMENTS,
  FAVORITES,
  FOLDER_CONTENTS_PATH,
  TEAM_TEMPLATES,
  SHARED_WITH_ME_TEMPLATES,
  REMOVE_BACKGROUND,
  CREATE_DESIGN_VIDEO_TEMPLATE,
  REVIEW_PACKAGE,
  IMAGE_CONVERTER,
  SEARCH_TEMPLATES_BY_FORMAT_SCREEN_PATH,
  AI_WRITER_SCREEN_PATH,
  VIDEO_CONVERTER,
  SNAPSHOT_MAKER_TWEET_TO_IMAGE,
  SNAPSHOT_MAKER_GENERATE_PREVIEW,
  AI_SHORT,
  AI_SHORT_FORM_ALL,
} from "../_utils/routes";

import {
  logoutUser,
  setUserData,
  syncWorkspaces,
} from "../_actions/authActions";
import { GET_USER_INFO } from "../_actions/endpoints";
import PrivateRoute from "../_components/common/PrivateRoute";
import internalServer from "../_components/common/InternalServer";
import { getAuthToken, setAxiosAuthHeaders } from "../_utils/common";
import Tldr404 from "./Tldr404";
import Tldr403 from "./Tldr403";
import Callback from "../TldrSettings/Callback";
import ScrollToTop from "../_components/common/ScrollToTop";
import {
  PasswordResetFailure,
  PasswordResetSuccess,
  PasswordSetSuccessViaInvitation,
  PasswordSetFailureViaInvitation,
  RegistrationVerificationFailure,
  RegistrationVerificationSuccess,
} from "../TldrRegistration/statelessView";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import TldrOauthWaitingPage from "./TldrOauthWaitingPage";
import Tldr401 from "./Tldr401";
import TldrMobileState from "../_components/common/TldrMobileState";
import { URLSearchParamsCustom } from "../_utils/common";
import StoryDetailsPage from "../TldrStoryDetail/StoryDetailsPage";
import useComponentWillMount from "../_hooks/useComponentWillMount";
import { setRBWorkspacePermissionCheck } from "../_actions/permissionActions";
import Home from "../TldrApp/Home";

import Login from "../TldrLogin/Login";
import ResetPasswordEmailSentNotification from "../TldrRegistration/ResetPasswordEmail";
import Register from "../TldrRegistration/Register";
import ResetPassword from "../TldrRegistration/ResetPassword";
import ConfirmYourEmailAddres from "../TldrRegistration/ConfirmYourEmailAddress";
import ForgotPassword from "../TldrRegistration/ForgotPassword";
import SetPasswordViaInvitation from "../TldrRegistration/SetPasswordViaInvitation";
import RegistrationEmailSentNotification from "../TldrRegistration/RegistrationEmail";

import ErrorBoundary from "../_components/common/ErrorBoundary";
import AutoCreateDesignProject from "./AutoCreateDesignVideoProject ";
import { theme } from "@simplified-workspace/shared/util-theme";

const Preview = React.lazy(() => import("../TldrPreview/Preview"));
const RenderPreview = React.lazy(() => import("../TldrPreview/RenderPreview"));
const Brandkits = React.lazy(() =>
  import("../TldrSettings/TldrBrandKits/Brandkits")
);
const TldrAIDetailsPage = React.lazy(() =>
  import("../TldrAi/TldrAIDetailsPage")
);
const AddMembers = React.lazy(() => import("../TldrWorkspace/AddMembers"));
const TldrSumo = React.lazy(() => import("../TldrPartners/TldrSumo"));

const Settings = React.lazy(() => import("../TldrSettings/Settings"));
const CreateWorkspace = React.lazy(() =>
  import("../TldrWorkspace/CreateWorkspace")
);
const PlannerNewPost = React.lazy(() =>
  import("../_containers/extension/PlannerNewPost/PlannerNewPost")
);
const ReviewPackage = React.lazy(() =>
  import("../_containers/ReviewPackage/ReviewPackage")
);
const ImageConvert = React.lazy(()=>
  import("../tools/ImageConverter")
);
const RemoveBackgroundTool = React.lazy(() =>
  import("../tools/RemoveBackground")
);

const VideoConvert = React.lazy(()=>
import("../tools/VideoConverter")
);

const SnapshotMakerTool = React.lazy(() =>
  import("../tools/SnapshotMakerTool")
);
const SnapshotMakerGeneratePreview = React.lazy(() =>
  import("../tools/SnapshotMakerGeneratePreview")
);

const AnalyticsGenerateReport = React.lazy(() =>
  import("../_containers/Analytics/GenerateReport")
);

//in case URLSearchParams doesn't exist
URLSearchParamsCustom(window);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

if (process.env.REACT_APP_ENV !== "local") {
  Sentry.init({
    dsn: "https://8531556389fc4472bfb86e5864603d61@o525022.ingest.sentry.io/5649360",
    integrations: [new Integrations.BrowserTracing()],

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.25,
    release: `${process.env.REACT_APP_NAME}@${process.env.REACT_APP_VERSION}`,
    normalizeDepth: 10,
    environment: process.env.REACT_APP_ENV,
  });
}

const NoMatchRoute = () => <Tldr404 />;

const AuthUser = ({ children }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [initialized, setInitialized] = useState(false);

  useComponentWillMount(() => {
    const token = getAuthToken();
    const workspaceId = auth.payload?.selectedOrg;

    if (token && token !== "undefined") {
      setAxiosAuthHeaders(token, workspaceId);

      axios
        .get(GET_USER_INFO)
        .then((res) => {
          // update-to-date the user and orgs on the page refresh
          const { orgs, ...userData } = res.data;

          if (res.data) {
            // @NOTE the /user request return only basic orgs data so we don't set it to the redux from this request
            dispatch(setUserData(userData));
          }

          // @NOTE refresh the orgs data (with correct subscription data)
          dispatch(syncWorkspaces());
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            dispatch(logoutUser());
          }
        });
    } else if (auth.isAuthenticated) {
      dispatch(logoutUser(UNAUTHORIZED));
    }
  });

  useEffect(() => {
    if (initialized) {
      // @NOTE get the new user data after switch workspace to have the correct "role" data
      axios.get(GET_USER_INFO).then((res) => {
        const { orgs, ...userData } = res.data;

        if (res.data) {
          // @NOTE the /user request return only basic orgs data so we don't set it to the redux from this request
          dispatch(setUserData(userData));
        }

        // @NOTE we don't need to update the orgs data after switch workspace but if we need to be sure to have updated data then we can uncomment this line
        dispatch(syncWorkspaces());
      });
    } else {
      setInitialized(true);
    }
  }, [auth.payload?.selectedOrg]);

  useEffect(() => {
    dispatch(setRBWorkspacePermissionCheck(auth.payload?.user));
  }, [auth.payload?.user]);

  return children;
};

const App = () => {
  
  return(
  <Elements stripe={stripePromise}>
    <Provider store={store}>
      <ChakraProvider theme={theme} resetCSS={true}>
        <Suspense fallback={<div>Loading...</div>}>
          <ErrorBoundary>
            <AuthUser>
              <Router>
                <ScrollToTop />

                <TldrMobileState />

                <Switch>
                  <Route
                    exact
                    path={REGISTRATION_EMAIL_SENT}
                    component={RegistrationEmailSentNotification}
                  />
                  <Route
                    exact
                    path={CONFIRM_EMAIL}
                    component={ConfirmYourEmailAddres}
                  />
                  <Route
                    exact
                    path={REGISTRATION_VERIFICATION_SUCCESS}
                    component={RegistrationVerificationSuccess}
                  />
                  <Route
                    exact
                    path={REGISTRATION_VERIFICATION_FAILURE}
                    component={RegistrationVerificationFailure}
                  />
                  <Route
                    exact
                    path={PASSWORD_RESET_EMAIL_SENT}
                    component={ResetPasswordEmailSentNotification}
                  />
                  <Route
                    exact
                    path={CONFIRM_PASSWORD_RESET}
                    component={ResetPassword}
                  />
                  <Route
                    exact
                    path={PASSWORD_RESET_SUCCESS}
                    component={PasswordResetSuccess}
                  />
                  <Route
                    exact
                    path={PASSWORD_RESET_FAILURE}
                    component={PasswordResetFailure}
                  />
                  <Route
                    exact
                    path={CREATE_PASSWORD_VIA_INVITATION}
                    component={SetPasswordViaInvitation}
                  />
                  <Route
                    exact
                    path={PASSWORD_SET_SUCCESS_VIA_INVITATION}
                    component={PasswordSetSuccessViaInvitation}
                  />
                  <Route
                    exact
                    path={PASSWORD_SET_FAILURE_VIA_INVITATION}
                    component={PasswordSetFailureViaInvitation}
                  />
                  <Route
                    exact
                    path={SNAPSHOT_MAKER_TWEET_TO_IMAGE}
                    component={SnapshotMakerTool}
                  />
                  <Route
                    exact
                    path={SNAPSHOT_MAKER_GENERATE_PREVIEW}
                    component={SnapshotMakerGeneratePreview}
                  />
                  <Route
                    exact
                    path={REMOVE_BACKGROUND}
                    component={RemoveBackgroundTool}
                  />
                  <PrivateRoute exact path={ROOT} component={Home} />
                  <Route exact path={LOGIN} component={Login} />
                  <Route exact path={LIFETIME} component={TldrSumo} />
                  <Route exact path={REGISTER_PATH} component={Register} />
                  <Route
                    exact
                    path={FORGOT_PASSWORD_PATH}
                    component={ForgotPassword}
                  />
                  <PrivateRoute exact path={QUICK_START} component={Home} />
                  <PrivateRoute exact path={USER_HOME} component={Home} />
                  <PrivateRoute exact path={PROJECTS} component={Home} />
                  <PrivateRoute exact path={MY_PROJECTS} component={Home} />
                  <PrivateRoute
                    exact
                    path={SHARED_WITH_ME_PROJECTS}
                    component={Home}
                  />
                  <PrivateRoute exact path={MY_ASSETS} component={Home} />
                  <PrivateRoute exact path={TEAM_TEMPLATES} component={Home} />
                  <PrivateRoute exact path={MY_TEMPLATES} component={Home} />
                  <PrivateRoute
                    exact
                    path={SHARED_WITH_ME_TEMPLATES}
                    component={Home}
                  />
                  <PrivateRoute exact path={MY_COMPONENTS} component={Home} />
                  <PrivateRoute exact path={LAYOUTS} component={Home} />
                  <PrivateRoute exact path={STORY} component={Home} />
                  <PrivateRoute exact path={MY_FOLDERS} component={Home} />
                  <PrivateRoute exact path={AI_SHORT} component={Home} />
                  <PrivateRoute exact path={AI_SHORT_FORM_ALL} component={Home} />
                  <PrivateRoute
                    exact
                    path={FOLDER_CONTENTS_PATH}
                    component={Home}
                  />

                  <PrivateRoute
                    exact
                    path={ALL_TEMPLATES_BY_FORMAT_PATH}
                    component={Home}
                  />
                  <PrivateRoute
                    exact
                    path={TEMPLATES_SCREEN}
                    component={Home}
                  />
                  <PrivateRoute exact path={PUBLISHING} component={Home} />
                  <PrivateRoute exact path={PLANNER} component={Home} />
                  <PrivateRoute exact path={ANALYTICS} component={Home} />
                  <Route exact path={ANALYTICS_GENERATE_REPORT} component={AnalyticsGenerateReport} />
                  <PrivateRoute exact path={DRAFT} component={Home} />
                  <PrivateRoute exact path={POST} component={Home} />
                  <PrivateRoute
                    exact
                    path={SEARCH_TEMPLATES_BY_FORMAT_SCREEN_PATH}
                    component={Home}
                  />
                  <PrivateRoute
                    exact
                    path={SEARCH_TEMPLATES_SCREEN}
                    component={Home}
                  />

                  <PrivateRoute
                    exact
                    path={TEMPLATES_BY_CATEGORY_SCREEN_PATH}
                    component={Home}
                  />
                  <PrivateRoute
                    exact
                    path={TEMPLATES_BY_FORMAT_SCREEN_PATH}
                    component={Home}
                  />
                  <PrivateRoute exact path={MY_COPIES} component={Home} />

                  <PrivateRoute exact path={MAGICAL} component={Home} />
                  <PrivateRoute exact path={DOCUMENTS} component={Home} />
                  <PrivateRoute exact path={MY_DOCUMENTS} component={Home} />

                  <PrivateRoute
                    exact
                    path={CREATE_WORKSPACE}
                    component={CreateWorkspace}
                  />
                  <PrivateRoute
                    exact
                    path={CREATE_DESIGN_VIDEO_TEMPLATE}
                    component={AutoCreateDesignProject}
                  />
                  <PrivateRoute
                    exact
                    path={SHARED_WITH_ME_DOCUMENTS}
                    component={Home}
                  />
                  <PrivateRoute exact path={FAVORITES} component={Home} />

                  <PrivateRoute
                    exact
                    path={AI_TEMPLATE_PATH}
                    component={TldrAIDetailsPage}
                  />
                  <PrivateRoute
                    exact
                    path={AI_DOCUMENT_PATH}
                    component={TldrAIDetailsPage}
                  />
                  <PrivateRoute exact path={AI_NEW_DOCUMENT} component={Home} />
                  <PrivateRoute
                    exact
                    path={ADD_NEW_MEMBERS}
                    component={AddMembers}
                  />
                  <PrivateRoute
                    exact
                    path={STORY_DETAIL_PATH}
                    component={StoryDetailsPage}
                  />
                  <PrivateRoute
                    exact
                    path={STORY_DETAIL_NAME_PATH}
                    component={StoryDetailsPage}
                  />
                  <PrivateRoute
                    path={STORY_PAGE_PATH}
                    component={StoryDetailsPage}
                  />
                  <PrivateRoute
                    exact
                    path={AI_WRITER_SCREEN_PATH}
                    component={Home}
                  />


                  <Route path={PREVIEW_PATH} component={Preview} />
                  <Route path={RENDER_PREVIEW} component={RenderPreview} />
                  <Route
                    exact
                    path={OAUTH_SHOPIFY_CALLBACK}
                    component={Callback}
                  />
                  <PrivateRoute path={SETTINGS} component={Settings} />

                  <PrivateRoute exact path={BRANDKIT} component={Brandkits} />
                  <PrivateRoute
                    exact
                    path={SHOPIFY_LOGIN}
                    component={Settings}
                  />

                  <Route path={IMAGE_CONVERTER} component={ImageConvert} />
                  <Route path={VIDEO_CONVERTER} component={VideoConvert}/>

                  <Route
                    path={OAUTH_GENERIC_WAITING_SCREEN}
                    component={TldrOauthWaitingPage}
                  />
                  <Route
                    path={EXTENSION_PLANNER_NEW_POST}
                    component={PlannerNewPost}
                  />
                  <Route path={REVIEW_PACKAGE} component={ReviewPackage} />
                  <Route
                    path={INTERNAL_SERVER_ERROR}
                    component={internalServer}
                  />
                  <Route path={ACCESS_DENIED} component={Tldr403} />
                  <Route path={UNAUTHORIZED} component={Tldr401} />
                  <Route path={NOT_FOUND} component={NoMatchRoute} />
                </Switch>
              </Router>
            </AuthUser>
          </ErrorBoundary>
        </Suspense>
      </ChakraProvider>
    </Provider>
  </Elements>
);
}

export default App;
